import React, { useState } from 'react';
import SearchableDropdown from './SearchableDropdown';
import { PROJECT_TYPE_OPTIONS } from '../api/projects';
import './ProjectTypePicker.css';

export function sortProjectTypesForDisplay(types: string[]): string[] {
  const order = new Map<string, number>(PROJECT_TYPE_OPTIONS.map((t, i) => [t, i]));
  return Array.from(new Set(types)).sort((a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999));
}

interface ProjectTypePickerProps {
  value: string[];
  onChange: (types: string[]) => void;
  disabled?: boolean;
  addPlaceholder?: string;
}

/**
 * Multi-select project types (chips + searchable add), same interaction pattern as TaskAssigneePicker.
 */
const ProjectTypePicker: React.FC<ProjectTypePickerProps> = ({
  value,
  onChange,
  disabled = false,
  addPlaceholder = 'Search type to add…',
}) => {
  const [addField, setAddField] = useState('');
  const orderedSelected = sortProjectTypesForDisplay(value);
  const availableOptions = PROJECT_TYPE_OPTIONS.filter((t) => !value.includes(t));

  const tryAddLabel = (display: string) => {
    const trimmed = display.trim();
    if (!trimmed) return;
    const exact = PROJECT_TYPE_OPTIONS.find((t) => t === trimmed);
    if (exact && !value.includes(exact)) {
      onChange(sortProjectTypesForDisplay([...value, exact]));
      setAddField('');
    }
  };

  const remove = (t: string) => {
    onChange(value.filter((x) => x !== t));
  };

  return (
    <div className="project-type-picker">
      <div className="project-type-picker-chips">
        {orderedSelected.length === 0 ? (
          <span className="project-type-picker-empty">No types selected</span>
        ) : (
          orderedSelected.map((t) => (
            <span key={t} className="project-type-chip">
              {t}
              <button
                type="button"
                className="project-type-chip-remove"
                disabled={disabled}
                onClick={() => remove(t)}
                aria-label={`Remove ${t}`}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
      <SearchableDropdown
        options={[...availableOptions]}
        value={addField}
        onChange={(v) => {
          setAddField(v);
          tryAddLabel(v);
        }}
        disabled={disabled}
        placeholder={availableOptions.length === 0 ? 'All types selected' : addPlaceholder}
      />
    </div>
  );
};

export function ProjectTypePills({ types }: { types: string[] }) {
  const ordered = sortProjectTypesForDisplay(types);
  if (ordered.length === 0) {
    return <span className="project-type-pills-empty">—</span>;
  }
  return (
    <div className="project-type-pills" role="list">
      {ordered.map((t) => (
        <span key={t} className="project-type-pill" role="listitem">
          {t}
        </span>
      ))}
    </div>
  );
}

export default ProjectTypePicker;
